/**
 * Additional work on an active job: the shapes fn_get_job_authorisation
 * returns, and the presentation rules for them.
 *
 * No money is decided here. The approved total, each request's amount and the
 * VAT on it all come from the database (fn_get_job_authorisation,
 * fn_additional_work_save). The one sum this file does is the live preview
 * while a mechanic is still typing, before anything is saved.
 */

export type AdditionalWorkState =
    | 'draft'
    | 'sent'
    | 'accepted'
    | 'declined'
    | 'superseded'
    | 'cancelled';

export interface AdditionalWorkLine {
    type: 'part' | 'labour';
    description: string;
    quantity: number;
    unitPrice: number;
    costPrice?: number;
}

export interface AdditionalWorkRequest {
    id: string;
    sequence_no: number;
    state: AdditionalWorkState;
    amount: number;
    line_items: AdditionalWorkLine[];
    notes: string | null;
    photos: string[];
    public_token: string;
    supersedes_quote_id: string | null;
    created_at: string;
    sent_at: string | null;
    accepted_at: string | null;
    rejected_at: string | null;
    rejection_reason: string | null;
    voided_at: string | null;
}

export interface JobAuthorisation {
    job_id: string;
    original: { quote_id: string | null; amount: number; from_job_items?: boolean } | null;
    additional_work: AdditionalWorkRequest[];
    accepted_additions: number;
    awaiting_customer: number;
    /** What the customer agreed to: frozen at the prices they accepted. */
    approved_total: number;
    /**
     * The job's lines as they stand now, gross. Moves when the mechanic edits
     * the job's own lines (labour came in under the quote); the invoice is
     * built from these, not from approved_total.
     */
    current_total: number;
    /** Why no more work can be added or decided (invoiced, paid, void), or null. */
    blocked_reason: string | null;
    /** The VAT rate fn_additional_work_save applies to this job (0 or 0.2). */
    vat_rate: number;
}

export const STATE_LABEL: Record<AdditionalWorkState, string> = {
    draft: 'Draft',
    sent: 'Awaiting customer',
    accepted: 'Accepted',
    declined: 'Declined',
    superseded: 'Replaced',
    cancelled: 'Cancelled',
};

/** Tone keys map to the badge colours in AdditionalWorkPanel. */
export const STATE_TONE: Record<AdditionalWorkState, 'muted' | 'amber' | 'success' | 'danger'> = {
    draft: 'muted',
    sent: 'amber',
    accepted: 'success',
    declined: 'danger',
    superseded: 'muted',
    cancelled: 'muted',
};

/**
 * Requests worth showing in the job's list. A request replaced by a revision
 * is history, not something to act on: the revision carries its number.
 */
export function visibleRequests(all: readonly AdditionalWorkRequest[]): AdditionalWorkRequest[] {
    return all.filter((r) => r.state !== 'superseded');
}

/** Which actions a mechanic has on a request. Decided requests have none. */
export function requestActions(r: Pick<AdditionalWorkRequest, 'state'>, blocked: boolean) {
    if (blocked) return { edit: false, send: false, revise: false, cancel: false };
    return {
        edit: r.state === 'draft',
        send: r.state === 'draft' || r.state === 'sent',
        revise: r.state === 'sent',
        cancel: r.state === 'draft' || r.state === 'sent',
    };
}

/**
 * The private bucket's policies scope on the FIRST path segment (the org), and
 * fn_additional_work_clean_photos only accepts this exact folder for the job.
 */
export function additionalWorkPhotoPath(orgId: string, jobId: string, fileName: string): string {
    const ext = (fileName.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 5) || 'jpg';
    const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${orgId}/additional-work/${jobId}/${id}.${ext}`;
}

/**
 * Live preview while typing. The saved figure is the server's
 * (fn_additional_work_total); vatRate is the job's rate as the server decided
 * it (fn_get_job_financials), never recomputed from settings here.
 */
export function previewGross(lines: readonly Pick<AdditionalWorkLine, 'quantity' | 'unitPrice'>[], vatRate: number): number {
    // fn_additional_work_clean_items stores quantity and price at 2 dp first,
    // then the total rounds each line, then the VAT on the net.
    const net = lines.reduce((s, l) => s + round2(round2(l.quantity || 0) * round2(l.unitPrice || 0)), 0);
    return round2(net + round2(net * vatRate));
}

/** Half away from zero, like Postgres numeric round(x, 2). */
function round2(n: number): number {
    return Math.sign(n) * Math.round((Math.abs(n) + Number.EPSILON) * 100) / 100;
}

export const money = (n: number | null | undefined) =>
    `£${(Number.isFinite(Number(n)) ? Number(n) : 0).toFixed(2)}`;
